import { Skeleton, Table } from '@radix-ui/themes';
import { Table as TanstackTable, flexRender } from '@tanstack/react-table';
import { t } from 'i18next';

import { FlyEmptyState } from '../../FlyEmptyState/FlyEmptyState';
import { getColumnMeta } from '../utils/getColumnHtmlMeta';
import { SkeletonCell } from './SkeletonCell';
import { TanstackTableHeader } from './TanstackTableHeader';

export function TanstackTableView<T extends Record<string, any>>({
  table,
  isFilterDataEmpty,
  isDataEmpty,
  loading,
  skeletonRows,
  emptyStateElement,
  layout,
  ...rest
}: {
  table: TanstackTable<T>;
  isFilterDataEmpty: boolean;
  isDataEmpty: boolean;
  loading: boolean;
  skeletonRows: number;
  emptyStateElement: React.ReactNode;
  layout: 'fixed' | 'auto';
} & React.ComponentProps<typeof Table.Root>) {
  let tableBody: React.ReactNode = null;

  // Show skeleton first when loading (initial load or refetch e.g. after filter change),
  // so we don't show stale data and avoid layout jump when new data arrives.
  if (loading) {
    tableBody = Array.from({ length: skeletonRows }).map((_, index) => (
      <Table.Row key={index}>
        {table.getAllColumns().map((column) => {
          const { skeletonConfig, htmlProps } = getColumnMeta(
            column.columnDef.meta
          );
          return (
            <Table.Cell key={column.id} {...htmlProps} className='!p-2'>
              {skeletonConfig ? (
                <SkeletonCell config={skeletonConfig} />
              ) : (
                <Skeleton width='100%' height='100%' />
              )}
            </Table.Cell>
          );
        })}
      </Table.Row>
    ));
  } else if (table.getRowModel().rows.length > 0) {
    tableBody = table.getRowModel().rows.map((row) => (
      <Table.Row key={row.id}>
        {row.getVisibleCells().map((cell) => {
          const { htmlProps } = getColumnMeta(cell.column.columnDef.meta);
          return (
            <Table.Cell key={cell.id} {...htmlProps}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Table.Cell>
          );
        })}
      </Table.Row>
    ));
  } else if (isFilterDataEmpty) {
    tableBody = (
      <Table.Row>
        <Table.Cell colSpan={table.getAllColumns().length}>
          <FlyEmptyState
            title={t(
              'app.dashboard.runtime_environments.environments.no_data_found_filter.title',
              {
                defaultValue: 'No Results',
              }
            )}
            description={t(
              'app.dashboard.runtime_environments.environments.no_data_found_filter.subtitle',
              {
                defaultValue: 'Remove or adjust your search filters',
              }
            )}
          />
        </Table.Cell>
      </Table.Row>
    );
  } else if (isDataEmpty) {
    tableBody = (
      <Table.Row>
        <Table.Cell colSpan={table.getAllColumns().length}>
          {emptyStateElement ?? <FlyEmptyState title='No data found' />}
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <div
      style={{
        overflow: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--gray-a8) transparent',
      }}
      className='w-full'
    >
      <Table.Root
        {...rest}
        variant='surface'
        className='min-h-0 bg-transparent'
        layout={layout}
      >
        <TanstackTableHeader table={table} />
        <Table.Body className='bg-transparent'>{tableBody}</Table.Body>
      </Table.Root>
    </div>
  );
}
